import { Modal, message, Select } from 'antd';
import { format } from 'date-fns';
import { Eye, RefreshCcw, Send } from 'lucide-react';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type Column, DataTable } from '@/components/common/DataTable';
import { Pagination } from '@/components/common/Pagination';
import { SearchBar } from '@/components/common/SearchBar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  brandStore,
  type LandingWaitlistEntry,
  landingWaitlistStore,
} from '@/stores';

function formatDateTime(value: string) {
  if (!value) {
    return '-';
  }

  const numericValue = Number(value);
  const date = Number.isFinite(numericValue)
    ? new Date(numericValue * 1000)
    : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return format(date, 'yyyy-MM-dd HH:mm:ss');
}

const OCCUPATION_LABELS: Record<string, string> = {
  software_engineer: '软件工程师 / 开发者',
  ai_ml_engineer: 'AI / 机器学习工程师',
  product_manager: '产品经理',
  designer: '设计师',
  data_professional: '数据从业者',
  founder_entrepreneur: '创始人 / 创业者',
  team_leader: '团队负责人',
  marketing_operations: '市场 / 运营',
  researcher_educator: '研究者 / 教育工作者',
  student: '学生',
  other: '其他',
};

const CHANNEL_OPTIONS = [
  'unattributed',
  'direct',
  'github',
  'instagram',
  'tiktok',
  'wechat',
  'x',
  'xiaohongshu',
  'youtube',
  'unknown',
  'bilibili',
  'csdn',
  'dingtalk',
  'discord',
  'douyin',
  'facebook',
  'feishu',
  'hacker_news',
  'juejin',
  'kuaishou',
  'linkedin',
  'messenger',
  'pinterest',
  'product_hunt',
  'qq',
  'reddit',
  'slack',
  'snapchat',
  'teams',
  'telegram',
  'threads',
  'toutiao',
  'v2ex',
  'wecom',
  'weibo',
  'whatsapp',
  'zhihu',
].map((channel) => ({
  label: channel === 'unattributed' ? '未归因（历史记录）' : channel,
  value: channel,
}));

function QuestionnaireDetails({ entry }: { entry: LandingWaitlistEntry }) {
  const questionnaire = entry.questionnaire;
  if (!questionnaire) {
    return (
      <p className="text-sm text-muted-foreground">历史记录，无问卷内容。</p>
    );
  }
  if (
    questionnaire.formId === 'tutti-vm-waitlist' &&
    questionnaire.schemaVersion === 1
  ) {
    const occupation =
      typeof questionnaire.answers.occupation === 'string'
        ? questionnaire.answers.occupation
        : '';
    const otherText =
      typeof questionnaire.answers.occupationOtherText === 'string'
        ? questionnaire.answers.occupationOtherText
        : '';
    return (
      <dl className="space-y-4">
        <div>
          <dt className="text-xs text-muted-foreground">主要职业</dt>
          <dd className="mt-1 text-sm font-medium">
            {OCCUPATION_LABELS[occupation] || occupation || '-'}
          </dd>
        </div>
        {occupation === 'other' ? (
          <div>
            <dt className="text-xs text-muted-foreground">其他职业</dt>
            <dd className="mt-1 text-sm font-medium">{otherText || '-'}</dd>
          </div>
        ) : null}
      </dl>
    );
  }
  return (
    <div>
      <p className="mb-2 text-xs text-muted-foreground">
        未知问卷版本，以下为原始信封：
      </p>
      <pre className="max-h-96 overflow-auto rounded-md bg-slate-950 p-4 text-xs text-slate-100">
        {JSON.stringify(questionnaire, null, 2)}
      </pre>
    </div>
  );
}

export const LandingWaitlistPage = observer(() => {
  const [inviteDialogEntry, setInviteDialogEntry] =
    useState<LandingWaitlistEntry | null>(null);
  const [questionnaireEntry, setQuestionnaireEntry] =
    useState<LandingWaitlistEntry | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const prevQueryRef = useRef<{
    brandId: string;
    page: number;
    pageSize: number;
    searchQuery: string;
    channelFilter: string;
  } | null>(null);

  const activeBrand = brandStore.activeBrand;
  const activeBrandId = brandStore.activeBrandId;
  const page = landingWaitlistStore.pagination.page;
  const pageSize = landingWaitlistStore.pagination.pageSize;
  const searchQuery = landingWaitlistStore.searchQuery;
  const channelFilter = landingWaitlistStore.channelFilter;
  const sendingEmail = landingWaitlistStore.sendingEmail;
  const batchSending = landingWaitlistStore.batchSending;

  useEffect(() => {
    const prev = prevQueryRef.current;
    if (
      !prev ||
      prev.brandId !== activeBrandId ||
      prev.page !== page ||
      prev.pageSize !== pageSize ||
      prev.searchQuery !== searchQuery ||
      prev.channelFilter !== channelFilter
    ) {
      prevQueryRef.current = {
        brandId: activeBrandId,
        page,
        pageSize,
        searchQuery,
        channelFilter,
      };
      setSelectedRowKeys([]);

      void landingWaitlistStore.fetchEntries().catch((error) => {
        console.error('加载邮箱邀请码名单失败:', error);
        message.error('加载邮箱邀请码名单失败，请重试');
      });
    }
  }, [activeBrandId, page, pageSize, searchQuery, channelFilter]);

  const handleOpenInviteDialog = useCallback((entry: LandingWaitlistEntry) => {
    setInviteDialogEntry(entry);
  }, []);

  const handleCloseInviteDialog = useCallback(() => {
    if (landingWaitlistStore.sendingEmail) {
      return;
    }
    setInviteDialogEntry(null);
  }, []);

  const handleConfirmSendInvite = useCallback(async () => {
    if (!inviteDialogEntry) {
      return;
    }

    try {
      await landingWaitlistStore.sendInvite(inviteDialogEntry);
      message.success('邀请码发送成功');
      setInviteDialogEntry(null);
    } catch (error) {
      console.error('发送邮箱邀请码失败:', error);
      message.error('发送邮箱邀请码失败，请重试');
    }
  }, [inviteDialogEntry]);

  const handleBatchSend = useCallback(async () => {
    const selected = landingWaitlistStore.entries.filter((entry) =>
      selectedRowKeys.includes(entry.id),
    );
    if (selected.length === 0) return;
    try {
      const result = await landingWaitlistStore.batchSendInvites(selected);
      setSelectedRowKeys([]);
      if (result.failedCount > 0) {
        message.warning(
          `批量发送完成：成功 ${result.sentCount}，失败 ${result.failedCount}`,
        );
      } else {
        message.success(`已发送 ${result.sentCount} 封邀请码邮件`);
      }
    } catch (error) {
      console.error('批量发送邮箱邀请码失败:', error);
      message.error('批量发送失败，请重试');
    }
  }, [selectedRowKeys]);

  const columns = useMemo<Column<LandingWaitlistEntry>[]>(
    () => [
      {
        id: 'email',
        header: '邮箱',
        accessor: (row) => <span className="font-medium">{row.email}</span>,
      },
      {
        id: 'source',
        header: '提交入口',
        accessor: (row) => row.source || '-',
      },
      {
        id: 'channel',
        header: '获客渠道',
        accessor: (row) => row.attribution?.channel || '-',
      },
      {
        id: 'trafficContentId',
        header: '活动标识',
        accessor: (row) => row.attribution?.trafficContentId || '-',
      },
      {
        id: 'inviteCode',
        header: '绑定邀请码',
        accessor: (row) => (
          <span className="font-mono text-sm">{row.inviteCode || '-'}</span>
        ),
      },
      {
        id: 'questionnaire',
        header: '问卷',
        accessor: (row) => (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setQuestionnaireEntry(row)}
          >
            <Eye className="mr-2 h-4 w-4" />
            {row.questionnaire ? '查看' : '历史空问卷'}
          </Button>
        ),
      },
      {
        id: 'status',
        header: '发放状态',
        accessor: (row) => (
          <Badge variant="outline">
            {row.status === 3
              ? '邮件已发送'
              : row.inviteCodeId
                ? '邀请码待发送'
                : '尚未生成'}
          </Badge>
        ),
      },
      {
        id: 'usedBy',
        header: '兑换用户',
        accessor: (row) => (
          <span className="font-mono text-sm">{row.usedBy || '未兑换'}</span>
        ),
      },
      {
        id: 'createdAt',
        header: '预约时间',
        accessor: (row) => formatDateTime(row.createdAt),
      },
      {
        id: 'updatedAt',
        header: '更新时间',
        accessor: (row) => formatDateTime(row.updatedAt),
      },
      {
        id: 'actions',
        header: '操作',
        accessor: (row) => (
          <Button
            variant="outline"
            size="sm"
            disabled={!row.inviteCodeId}
            loading={sendingEmail === row.email}
            onClick={() => handleOpenInviteDialog(row)}
          >
            <Send className="mr-2 h-4 w-4" />
            {row.status === 3
              ? '重发邀请码'
              : row.inviteCodeId
                ? '发送邀请码'
                : '无绑定邀请码'}
          </Button>
        ),
      },
    ],
    [handleOpenInviteDialog, sendingEmail],
  );

  return (
    <div className="rounded-lg border border-border bg-white shadow-sm">
      <div className="space-y-6 p-6">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-lg font-semibold text-foreground">
                邮箱邀请码名单
              </h1>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700">
                {activeBrand?.name || '未选择 Brand'}
              </span>
            </div>
            <p className="max-w-3xl text-sm text-muted-foreground">
              这里管理 landing 页提交的邮箱预约记录，和内测 Waitlist
              申请审批流分开维护。
            </p>
          </div>
          <Button
            variant="outline"
            onClick={() => void landingWaitlistStore.fetchEntries()}
          >
            <RefreshCcw className="mr-2 h-4 w-4" />
            刷新
          </Button>
        </div>

        <div className="flex flex-wrap items-end gap-4">
          <SearchBar
            placeholder="输入完整邮箱或绑定邀请码查询"
            onSearch={(keyword) => landingWaitlistStore.setSearchQuery(keyword)}
            className="max-w-md"
            loading={landingWaitlistStore.loading}
          />
          <Select
            allowClear
            showSearch
            placeholder="筛选获客渠道"
            options={CHANNEL_OPTIONS}
            value={channelFilter || undefined}
            onChange={(channel) =>
              landingWaitlistStore.setChannelFilter(channel || '')
            }
            className="min-w-44"
          />
          <Button
            variant="default"
            disabled={selectedRowKeys.length === 0}
            loading={batchSending}
            onClick={() => void handleBatchSend()}
          >
            <Send className="mr-2 h-4 w-4" />
            批量发送（{selectedRowKeys.length}）
          </Button>
        </div>

        <div className="space-y-4">
          <DataTable
            columns={columns}
            data={landingWaitlistStore.entries}
            loading={landingWaitlistStore.loading}
            emptyMessage="暂无邮箱预约记录"
            rowSelection={{
              selectedRowKeys,
              onChange: (keys) => setSelectedRowKeys(keys.map(String)),
              getCheckboxProps: (row) => ({ disabled: !row.inviteCodeId }),
            }}
          />
          <Pagination
            page={landingWaitlistStore.pagination.page}
            pageSize={landingWaitlistStore.pagination.pageSize}
            total={landingWaitlistStore.pagination.total}
            onPageChange={(nextPage) => {
              landingWaitlistStore.setPagination(nextPage);
            }}
          />
        </div>
      </div>

      <Modal
        title={inviteDialogEntry?.status === 3 ? '重发邀请码' : '发送邀请码'}
        open={!!inviteDialogEntry}
        okText={inviteDialogEntry?.status === 3 ? '重发' : '发送'}
        cancelText="取消"
        confirmLoading={
          !!inviteDialogEntry && sendingEmail === inviteDialogEntry.email
        }
        onCancel={handleCloseInviteDialog}
        onOk={() => void handleConfirmSendInvite()}
      >
        <div className="space-y-4 py-2">
          <div>
            <div className="text-sm text-muted-foreground">预约邮箱</div>
            <div className="mt-1 font-medium">{inviteDialogEntry?.email}</div>
          </div>
          <div>
            <div className="text-sm text-muted-foreground">当前绑定邀请码</div>
            <div className="mt-1 font-mono font-medium">
              {inviteDialogEntry?.inviteCode || '-'}
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              发送操作只会发送该记录已绑定的邀请码，不会生成或替换邀请码。
            </p>
          </div>
        </div>
      </Modal>

      <Modal
        title={`问卷详情 · ${questionnaireEntry?.email || ''}`}
        open={!!questionnaireEntry}
        footer={null}
        onCancel={() => setQuestionnaireEntry(null)}
      >
        {questionnaireEntry ? (
          <QuestionnaireDetails entry={questionnaireEntry} />
        ) : null}
      </Modal>
    </div>
  );
});
